import React, { useState } from 'react';
import { devLog, devWarn, devError } from '../../utils/devLogger';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { AppTextField } from '../../customComponents/AppTextField';
import { AppGPhoneTextField } from '../../customComponents/AppGPhoneTextField';
import { FormTitle } from '../../customComponents/FormTitle';
import PrimaryButton from '../../components/PrimaryButton';
import Modal from '../../components/Modal';
import ModalFooter from '../../components/ModalFooter';
import CenteredLayout from '../../layout/CenteredLayout';
import PageContainerLayout from '../../layout/PageContainerLayout';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import FormContainer from '../../customComponents/FormContainer';
import { findEmail } from '../../lib/api/user/userApi';
import { useLang } from '../../context/LangContext';
import { useDevice } from '../../context/DeviceContext';
import { maskEmail } from '../../utils/emailUtils';

// 다국어 텍스트 객체
const findEmailTexts = {
  ko: {
    title: '이메일 찾기',
    formTitle: '본인 확인',
    nameLabel: '이름',
    namePlaceholder: '이름을 입력해주세요',
    nameValidationError: '이름은 2글자 이상 입력해주세요',
    completeButton: '완료',
    modalTitle: '이메일정보',
    notFound: '입력하신 정보와 일치하는 회원정보를 찾을 수 없습니다.',
    serverError: '서버에 문제가 발생했습니다. 잠시 후 다시 시도해 주세요.',
    socialAccountMessage: '해당 계정은 {provider}로 가입되어 있습니다.',
    socialLoginPrompt: '{provider} 로그인을 진행해주세요.',
    emailFoundMessage: '회원님께서 가입하신 이메일은 아래와 같습니다.',
    closeButton: '닫기',
    loginButton: '로그인',
    confirmButton: '확인'
  },
  en: {
    title: 'Find Email',
    formTitle: 'Identity Verification',
    nameLabel: 'Name',
    namePlaceholder: 'Please enter your name',
    nameValidationError: 'Please enter at least 2 characters for name',
    completeButton: 'Complete',
    modalTitle: 'Email Information',
    notFound: 'No member information found matching the entered information.',
    serverError: 'A server problem occurred. Please try again later.',
    socialAccountMessage: 'This account is registered with {provider}.',
    socialLoginPrompt: 'Please proceed with {provider} login.',
    emailFoundMessage: 'Your registered email is as follows.',
    closeButton: 'Close',
    loginButton: 'Login',
    confirmButton: 'Confirm'
  }
};

// FindEmailContainer styles are now handled by PageContainerLayout

const FlexPageContainer = styled.div<{ $minHeight: string }>`
  display: flex;
  flex-direction: column;
  /* height: 100%; */
  min-height: ${({ $minHeight }) => $minHeight};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
`;

const CompleteButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: auto;
  padding: 24px 0 0 0;
`;

const EmailDisplay = styled.div`
  background-color: #f5f5f5;
  padding: 12px 16px;
  border-radius: 8px;
  font-family: monospace;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  text-align: center;
  margin: 16px 0;
`;

function FindEmail() {
  const navigate = useNavigate();
  const { lang } = useLang();
  const device = useDevice();
  const texts = findEmailTexts[lang];
  
  // 앱바 높이를 임의로 설정 (모바일: 60px, 웹: 90px)
  const appbarHeight = device === 'mobile' ? 120 : 250;
  const minHeight = `calc(100vh - ${appbarHeight}px)`;
  
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [isNameValid, setIsNameValid] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [foundEmail, setFoundEmail] = useState('');
  const [originalEmail, setOriginalEmail] = useState(''); // 원본 이메일 저장
  const [providerType, setProviderType] = useState<string | null>(null);

  const handleBack = () => {
    navigate(-1); // 이전 페이지로 이동
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;
    setName(value);
    setIsNameValid(value.trim().length >= 2); // 이름은 2글자 이상
  };

  const handlePhoneChange = (value: string, isValid: boolean) => {
    setPhone(value);
    setIsPhoneValid(isValid);
  };

  const handleComplete = async () => {
    devLog('이메일 찾기 완료:', { name, phone });
    
    try {
      const response = await findEmail({
        name: name.trim(),
        cellphone: phone
      });
      
      if (response.data && response.data.statusCode === 200) {
        // 성공: 모달로 이메일 표시
        const foundEmailAddress = response.data.data?.email || '';
        const providerType = response.data.data?.providerType;
        
        // 원본 이메일과 마스킹된 이메일 저장
        setOriginalEmail(foundEmailAddress);
        const maskedEmail = maskEmail(foundEmailAddress);
        setFoundEmail(maskedEmail);
        setIsModalOpen(true);
        
        // providerType 정보를 state에 저장하여 모달에서 사용
        setProviderType(providerType);
      } else {
        // API 응답이 있지만 실패 (404 등 클라이언트 에러)
        let errorMessage = texts.notFound;
        
        // 404 에러이거나 "not found" 메시지인 경우 한글 메시지 사용
        if (response.data?.statusCode === 404 || 
            response.data?.message === 'not found') {
          errorMessage = texts.notFound;
        } else if (response.data?.message) {
          errorMessage = response.data.message;
        }
        
        toast.error(errorMessage);
      }
    } catch (error) {
      // 500번대 서버 에러만 여기로 옴
      devError('이메일 찾기 서버 오류:', error);
      toast.error(texts.serverError);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleGoToLogin = () => {
    setIsModalOpen(false);
    navigate('/login', { 
      state: { 
        email: originalEmail // 원본 이메일 전달
      } 
    });
  };

  const isFormValid = isNameValid && isPhoneValid && 
                     name.trim() !== '' && phone.trim() !== '';

  return (
    <CenteredLayout>
      <ProjectAppbar 
        title={texts.title}
        showBackButton={true}
        onBack={handleBack}
      />
      <FormContainer>
        <PageContainerLayout>
          <FlexPageContainer $minHeight={minHeight}>
            <FormGroup>
              <FormTitle>{texts.formTitle}</FormTitle>
            
            <AppTextField
              value={name}
              onChange={handleNameChange}
              label={texts.nameLabel}
              placeholder={texts.namePlaceholder}
              errorMessage={name && !isNameValid ? texts.nameValidationError : ''}
              autoFocus={true}
              autoComplete="name"
            />

            <AppGPhoneTextField
              value={phone}
              onChange={handlePhoneChange}
              language={lang}
            />
          </FormGroup>

          <CompleteButtonContainer>
            <PrimaryButton
              onClick={handleComplete}
              disabled={!isFormValid}
            >
              {texts.completeButton}
            </PrimaryButton>
          </CompleteButtonContainer>
          </FlexPageContainer>
        </PageContainerLayout>

      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        bgColor="#ffffff"
        header={texts.modalTitle}
        body={
          <div>
            {providerType ? (
              // 소셜 로그인 계정인 경우
              <>
                <p style={{ marginBottom: '16px', textAlign: 'center', color: '#333' }}>
                  {texts.socialAccountMessage.replace('{provider}', providerType)}
                </p>
                <EmailDisplay>
                  {foundEmail}
                </EmailDisplay>
                <p style={{ marginTop: '16px', textAlign: 'center', color: '#666', fontSize: '14px' }}>
                  {texts.socialLoginPrompt.replace('{provider}', providerType)}
                </p>
              </>
            ) : (
              // 일반 이메일 가입인 경우
              <>
                <p style={{ marginBottom: '16px', textAlign: 'center', color: '#333' }}>
                  {texts.emailFoundMessage}
                </p>
                <EmailDisplay>
                  {foundEmail}
                </EmailDisplay>
              </>
            )}
          </div>
        }
        footer={
          <ModalFooter
            leftButtonText={texts.closeButton}
            rightButtonText={providerType ? texts.confirmButton : texts.loginButton}
            onLeftClick={handleModalClose}
            onRightClick={providerType ? handleModalClose : handleGoToLogin}
          />
        }
      />
      </FormContainer>
    </CenteredLayout>
  );
}

export default FindEmail;
